import { apiService } from '@clarix/api-client';

/**
 * AI app connections: OAuth grants an AI tool (claude.ai, ChatGPT, Cursor,
 * Claude Code) holds to act as one member in one workspace over MCP.
 *
 * The member surface (`/me/ai-connections`) reads the SESSION workspace; the
 * owner surface sits with the other admin endpoints under
 * `/workspaces/:slug/admin/*` and needs `manage_api_keys`. The workspace
 * on/off switch is not here: it lives in `apps/workspace` RTK with the other
 * security settings.
 */

export interface AiConnection {
  id: string;
  /** Self-declared by the AI app at registration. Unverified — render as text. */
  client_name: string;
  user_id: number;
  user_name: string | null;
  user_email: string | null;
  /** What the member allowed on the consent screen. */
  access: AiConnectionAccess;
  created_at: string;
  last_used_at: string | null;
}

export type AiConnectionAccess = 'read' | 'read_write';

export const AI_CONNECTION_ACCESS_LABEL: Record<AiConnectionAccess, string> = {
  read: 'Read only',
  read_write: 'Read and change',
};

export interface MyAiConnections {
  items: AiConnection[];
  /** This workspace's MCP URL, the one a member pastes into their AI app. */
  mcp_url: string;
  /** Off when an owner has switched AI app connections off for the workspace. */
  allow_ai_connections: boolean;
}

export interface WorkspaceAiConnections {
  items: AiConnection[];
  mcp_url: string;
}

export interface AiConnectionRevoked {
  id: string;
  revoked: true;
}

const adminBase = (slug: string) => `/workspaces/${slug}/admin/ai-connections`;

export const aiConnectionsApi = {
  listMine: () => apiService.get<MyAiConnections>('/me/ai-connections'),

  disconnectMine: (id: string) =>
    apiService.delete<AiConnectionRevoked>(`/me/ai-connections/${encodeURIComponent(id)}`),

  listWorkspace: (slug: string) => apiService.get<WorkspaceAiConnections>(adminBase(slug)),

  revokeWorkspace: (slug: string, id: string) =>
    apiService.delete<AiConnectionRevoked>(`${adminBase(slug)}/${encodeURIComponent(id)}`),
};
